import { FormFieldConfig } from "./types";

export const getOncologyFormConfig = (): FormFieldConfig[] => [
  {
    title: "Priority Health Precertification Documentation",
    fields: [
      {
        name: "nccnGuidelines",
        label:
          "Is the drug, including drug regimen, being utilized per the NCCN recommended use?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_nccn_guidelines_reviewed",
      },
      {
        name: "nccnGuidelinesName",
        label: "What NCCN Guidelines were reviewed for the request?",
        type: "text",
        required: false,
        placeholder: "Enter NCCN Guidelines name",
        dependsOn: "nccnGuidelines",
        dependsValue: "yes",
        firebaseKey:
          "submission_required_details.medicare_oncology_nccn_guidelines_name",
      },
      {
        name: "nccnCategory",
        label:
          "Is the drug, including drug regimen, listed as Category 1 or 2A recommendation in NCCN?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_nccn_category_1_2A",
      },
      {
        name: "reviewedMBDL",
        label:
          "Have you reviewed the Medical Benefit Drug List (MBDL) to see if the drug has step therapy and/or other prior authorization requirements?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes. Continue through questions." },
          {
            value: "no",
            label:
              "No. Refer to the MBDL to ensure all necessary information is provided to support a timely review.",
          },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_mbdl_reviewed",
      },
      {
        name: "medicalRecordsProvided",
        label:
          "Have medical records supporting the request been provided in line with the NCD and/or LCD?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_records_provided",
      },
      {
        name: "ecogScore",
        label: "What is the patient's ECOG score?",
        type: "select",
        required: false,
        options: [
          { value: "", label: "Select ECOG score" },
          { value: "0", label: "0" },
          { value: "1", label: "1" },
          { value: "2", label: "2" },
          { value: "3", label: "3" },
          { value: "4", label: "4" },
        ],
        firebaseKey: "submission_required_details.medicare_oncology_ecog_score",
      },
      {
        name: "cancerStage",
        label: "What is the cancer stage?",
        type: "select",
        required: false,
        options: [
          { value: "", label: "Select stage" },
          { value: "1", label: "1" },
          { value: "2", label: "2" },
          { value: "3", label: "3" },
          { value: "4", label: "4" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_cancer_stage",
      },
      {
        name: "cancerStageT",
        label: "T Stage",
        type: "text",
        required: false,
        placeholder: "T stage",
        firebaseKey: "submission_required_details.medicare_oncology_t_stage",
      },
      {
        name: "cancerStageN",
        label: "N Stage",
        type: "text",
        required: false,
        placeholder: "N stage",
        firebaseKey: "submission_required_details.medicare_oncology_n_stage",
      },
      {
        name: "cancerStageM",
        label: "M Stage",
        type: "text",
        required: false,
        placeholder: "M stage",
        firebaseKey: "submission_required_details.medicare_oncology_m_stage",
      },
      {
        name: "therapySequence",
        label: "Is this first, second, or subsequent therapy for the stage?",
        type: "select",
        required: false,
        options: [
          { value: "", label: "Select therapy sequence" },
          { value: "first", label: "First" },
          { value: "second", label: "Second" },
          { value: "subsequent", label: "Subsequent" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_therapy_line",
      },
    ],
  },
  {
    title: "Previous Treatment History (Section H)",
    fields: [
      {
        name: "previousTreatments",
        label:
          "List the other treatment tried and failed with the associated outcome (failure, intolerance, etc.)",
        type: "previousTreatments",
        required: false,
        firebaseKey:
          "submission_required_details.medicare_oncology_failed_treatments",
      },
    ],
  },
  {
    title: "Condition-Specific Questions (Section I)",
    fields: [
      {
        name: "requestedCondition",
        label:
          "What condition is this drug being requested for? Answer any applicable questions based on condition.",
        type: "select",
        required: false,
        options: [
          { value: "", label: "Select condition" },
          { value: "bladder_cancer", label: "Bladder Cancer" },
          { value: "breast_cancer", label: "Breast cancer" },
          {
            value: "chronic_myeloid_leukemia",
            label: "Chronic Myeloid Leukemia",
          },
          { value: "colon_cancer", label: "Colon cancer" },
          {
            value: "esophageal_cancer",
            label: "Esophageal / esophagogastric junction cancers",
          },
          { value: "gastric_cancer", label: "Gastric cancer" },
          { value: "head_neck_cancer", label: "Head and neck cancer" },
          {
            value: "hepatocellular_carcinoma",
            label: "Hepatocellular carcinoma (HCC)",
          },
          { value: "malignant_melanoma", label: "Malignant melanoma" },
          {
            value: "non_hodgkin_lymphomas",
            label: "Non-Hodgkin Lymphomas (B-cell lymphomas)",
          },
          { value: "nsclc", label: "Non-Small Cell Lung Cancer (NSCLC)" },
          { value: "prostate_cancer", label: "Prostate cancer" },
          { value: "rectal_cancer", label: "Rectal cancer" },
          {
            value: "renal_cell_carcinoma",
            label: "Renal cell carcinoma (RCC)",
          },
          { value: "small_cell_lung_cancer", label: "Small Cell Lung cancer" },
          { value: "other", label: "Other" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_cancer_type",
      },
      // Bladder Cancer questions
      {
        name: "bladderInvasive",
        label: "Is it muscle or non-muscle invasive?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "bladder_cancer",
        options: [
          { value: "muscle", label: "Muscle" },
          { value: "non_muscle", label: "Non-Muscle" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_bladder_invasive",
      },
      {
        name: "bladderPlatinumEligible",
        label: "Is the patient platinum eligible?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "bladder_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_bladder_platinum_eligible",
      },
      {
        name: "bladderPdl1Positive",
        label: "Is the patient PD-L1 positive?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "bladder_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_bladder_pdl1_positive",
      },
      // Breast Cancer questions
      {
        name: "breastErPrPositive",
        label: "Is it ER and/or PR-positive?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "breast_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_breast_er_pr_positive",
      },
      {
        name: "breastHer2Status",
        label: "Is it HER2 negative or positive?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "breast_cancer",
        options: [
          { value: "negative", label: "Negative" },
          { value: "positive", label: "Positive" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_breast_her2_status",
      },
      {
        name: "breastEndocrineTherapy",
        label: "Did patient receive endocrine therapy in the past year?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "breast_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_breast_endocrine_therapy_past_year",
      },
      // Chronic Myeloid Leukemia questions
      {
        name: "cmlPhase",
        label: "What phase of CML is the patient in?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "chronic_myeloid_leukemia",
        options: [
          { value: "chronic", label: "Chronic Phase" },
          { value: "accelerated", label: "Accelerated Phase" },
          { value: "blast", label: "Blast Phase" },
        ],
        firebaseKey: "submission_required_details.medicare_oncology_cml_phase",
      },
      {
        name: "cmlRisk",
        label: "What is the patient's risk score?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "chronic_myeloid_leukemia",
        options: [
          { value: "low", label: "Low" },
          { value: "intermediate", label: "Intermediate" },
          { value: "high", label: "High" },
        ],
        firebaseKey: "submission_required_details.medicare_oncology_cml_risk",
      },
      {
        name: "cmlImatinibTried",
        label: "Has imatinib been tried?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "chronic_myeloid_leukemia",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_cml_imatinib_tried",
      },
      // Colon Cancer questions
      {
        name: "colonUnresectable",
        label: "Is the cancer unresectable or metastatic?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "colon_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_colon_unresectable",
      },
      {
        name: "colonKrasWildType",
        label: "Is RAS wild-type?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "colon_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_colon_kras_wild_type",
      },
      {
        name: "colonTumorsLeftSided",
        label: "Are tumors left-sided?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "colon_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_colon_tumors_left_sided",
      },
      // Prostate Cancer questions
      {
        name: "prostateCastrationStatus",
        label: "What is the castration status?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "prostate_cancer",
        options: [
          { value: "sensitive", label: "Sensitive" },
          { value: "resistant", label: "Resistant" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_prostate_castration_resistant",
      },
      {
        name: "prostateAdenocarcinoma",
        label: "Is it adenocarcinoma?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "prostate_cancer",
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_prostate_is_adenocarcinoma",
      },
      {
        name: "prostateMetastasesType",
        label: "What type of metastases are present?",
        type: "radio",
        required: false,
        dependsOn: "requestedCondition",
        dependsValue: "prostate_cancer",
        options: [
          { value: "visceral", label: "Visceral" },
          { value: "bone", label: "Bone" },
          { value: "both", label: "Both" },
          { value: "none", label: "None" },
        ],
        firebaseKey:
          "submission_required_details.medicare_oncology_prostate_metastases",
      },
    ],
  },
];

export const getGeneralFormConfig = (): FormFieldConfig[] => [
  {
    title: "Priority Health Precertification Documentation",
    fields: [
      {
        name: "conditionRequested",
        label: "What condition is this drug being requested for?",
        type: "textarea",
        required: false,
        placeholder:
          "Enter the condition for which this drug is being requested",
        firebaseKey:
          "submission_required_details.medicare_non_oncology_medical_condition",
      },
      {
        name: "isCancerTherapy",
        label: "Is this a cancer therapy?",
        type: "radio",
        required: true,
        options: [
          {
            value: "yes",
            label:
              "Yes. Refer to the Medicare Part B Oncology Prior Authorization form to ensure all necessary information is provided for a timely review.",
          },
          { value: "no", label: "No. Continue through the questions." },
        ],
        firebaseKey:
          "submission_required_details.medicare_non_oncology_is_cancer_therapy",
      },
      {
        name: "reviewedMBDL",
        label:
          "Have you reviewed the Medical Benefit Drug List to see if this drug has Step Therapy and/or other prior authorization requirements?",
        type: "radio",
        required: true,
        options: [
          { value: "yes", label: "Yes. Continue through the questions." },
          {
            value: "no",
            label:
              "No. Refer to the Medical Benefit Drug List to ensure all necessary information is provided for a timely review",
          },
        ],
        firebaseKey:
          "submission_required_details.medicare_non_oncology_step_therapy_reviewed",
      },
      {
        name: "medicalReason",
        label: "Explain the medical reason for this request:",
        type: "textarea",
        required: true,
        placeholder:
          "Provide detailed medical justification for the drug request",
        firebaseKey:
          "submission_required_details.medicare_non_oncology_medical_reason_for_request",
      },
    ],
  },
  {
    title: "Previous Drug History (Section E)",
    fields: [
      {
        name: "previousDrugs",
        label:
          "List previous drugs the patient tried. (List the name, date prescribed, and any other important information.)",
        type: "previousDrugs",
        required: false,
        firebaseKey: "submission_required_details.previous_drugs",
      },
    ],
  },
  {
    title: "Additional Information (Section F)",
    fields: [
      {
        name: "additionalInformation",
        label:
          "Provide any additional information for consideration (optional):",
        type: "textarea",
        required: false,
        placeholder: "Any additional relevant information",
        firebaseKey:
          "submission_required_details.medicare_non_oncology_additional_information",
      },
    ],
  },
];
